import React, { useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import Button from 'core/elements/button'
import Theme from 'core/themes/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { ApplicationCredential } from './model'
import CopyToClipboard from 'core/components/CopyToClipboard'
import CodeBlock from 'core/components/CodeBlock'

const useStyles = makeStyles<Theme>((theme) => ({
  successHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  successIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.green[700],
    '& i': {
      color: theme.palette.grey['100'],
    },
    fontSize: 14,
  },
  section: {
    marginTop: theme.spacing(3),
  },
  copyContainer: {
    marginTop: theme.spacing(3),
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  fieldInput: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.grey[50],
    fontFamily: 'monospace',
    fontSize: 14,
  },
  inputText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  iconButton: {
    cursor: 'pointer',
    color: theme.palette.grey[500],
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}))

interface Props {
  credential: ApplicationCredential
  onClose: () => void
}

export default function SuccessModal({ credential, onClose }: Props) {
  const classes = useStyles()
  const [showSecret, setShowSecret] = useState(false)
  const authUrl = `${window.location.origin}/keystone/v3`

  const toggleSecretVisibility = useCallback(() => {
    setShowSecret((prev) => !prev)
  }, [])

  return (
    <Modal
      title="Create Application Credentials"
      onClose={onClose}
      open
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className={classes.successHeader}>
        <span className={classes.successIcon}>
          <FontAwesomeIcon solid size="sm">
            check
          </FontAwesomeIcon>
        </span>
        <Text variant="subtitle2">
          Congratulations! Your Application Credentials has been created.
        </Text>
      </div>

      <Text variant="body2">
        For security reasons, the <strong>password</strong> is displayed only once and can only be
        copied from this screen.
        <br />
        After you close this form, <strong>the password will no longer be accessible</strong>.
        <br />
        Please make sure to copy and store it securely before proceeding.
      </Text>

      <div className={classes.fieldRow}>
        <Text variant="body2">Application Credential ID</Text>
        <div className={classes.fieldInput}>
          <span className={classes.inputText}>{credential.id}</span>
          <CopyToClipboard copyText={credential.id} inline codeBlock={false} triggerWithChild>
            <FontAwesomeIcon className={classes.iconButton}>copy</FontAwesomeIcon>
          </CopyToClipboard>
        </div>
      </div>

      {credential.secret && (
        <div className={classes.fieldRow}>
          <Text variant="body2">Application Credential Key</Text>
          <div className={classes.fieldInput}>
            <span className={classes.inputText}>
              {showSecret ? credential.secret : '••••••••••••••'}
            </span>
            <FontAwesomeIcon className={classes.iconButton} onClick={toggleSecretVisibility}>
              {showSecret ? 'eye-slash' : 'eye'}
            </FontAwesomeIcon>
            <CopyToClipboard copyText={credential.secret} inline codeBlock={false} triggerWithChild>
              <FontAwesomeIcon className={classes.iconButton}>copy</FontAwesomeIcon>
            </CopyToClipboard>
          </div>
        </div>
      )}

      <div className={classes.section}>
        <Text variant="subtitle1">Environment Variables</Text>
        <Text variant="body2">
          Use these environment variables to authenticate with your application credential in pcdctl
          command-line interfaces.
        </Text>
        <CopyToClipboard
          className={classes.copyContainer}
          copyText={`export OS_AUTH_TYPE=v3applicationcredential
export OS_AUTH_URL=${authUrl}
export OS_APPLICATION_CREDENTIAL_ID=${credential.id}
export OS_APPLICATION_CREDENTIAL_SECRET=${credential.secret || '<your-secret>'}`}
        >
          <CodeBlock>
            {`export OS_AUTH_TYPE=v3applicationcredential
export OS_AUTH_URL=${authUrl}
export OS_APPLICATION_CREDENTIAL_ID=${credential.id}
export OS_APPLICATION_CREDENTIAL_SECRET=${credential.secret || '<your-secret>'}`}
          </CodeBlock>
        </CopyToClipboard>
      </div>
    </Modal>
  )
}
